<template>
  <v-card class="DepositDetails"
    @keydown.enter="save"
  >
    <v-card-title>
      <span v-if="isNewItem" class="subtitle-1">New Item</span>
      <span v-if="!isNewItem" class="subtitle-1">Edit Item</span>
    </v-card-title>
    <v-card-text>
      <v-form ref="form">
        <v-container :class="containerClass">
          <v-row>
            <v-col cols="2">
              <v-text-field class="odtrans fiscalYear input-text-size"
                id="odTransFiscalYear"
                dense
                hide-details="auto"
                v-model.number="internalValue.fiscalYear"
                label="Fiscal Year"
                type="number"
                @input="updateDeptAndNextTransNum"
                :rules="fiscalYearValidation"
                readonly
              ></v-text-field>
            </v-col>
            <v-col cols="4">
              <v-select class="odtrans type input-text-size"
                id="odTransTypeSelect"
                dense
                hide-details="auto"
                :readonly="isTransactionTypeReadOnly"
                v-model="internalValue.type"
                :items="filteredTransTypes"
                :rules="reqdSelectValidation"
                @change="determineNextTransactionNumbers()"
                item-text="description"
                item-value="name"
                label="Type"
              ></v-select>
            </v-col>
            <v-col cols="2">
              <v-tooltip bottom
                :open-on-hover="isTooltipVisible">
                <template v-slot:activator="{ on, attrs }">
                  <v-text-field class="odtrans registration input-text-size"
                    id="odTransRegistrationText"
                    dense
                    hide-details="auto"
                    v-bind="attrs"
                    v-on="on"
                    v-model.number="internalValue.registration"
                    label="Registration #"
                    color="red"
                    type="number"
                    :readonly="!registrationOverride || isNewItem"
                    :class="voucherRegPulseClass"
                    @dblclick.stop="overrideRegistrationNumber"
                  ></v-text-field>
                </template>
                <span>{{registrationHoverCaption}}</span>
              </v-tooltip>
            </v-col>
            <v-col cols="4">
              <ClarionDateControl class="odtrans register input-text-size"
                id="odTransRegisterDate"
                dense
                hide-details="auto"
                v-model="internalValue.register"
                :isRequired="true"
                :isIsoDate="true"
                :isToggle="false"
                :label="`Register Date`"
                :rules="[registerDateValidation, ...reqdSelectValidation]"
                @blurred="registerDateBlurred"
              />
            </v-col>
          </v-row>
          <v-row>
           <v-col>
        <v-autocomplete
          class="odtrans department input-text-size"
          id="odTransDepartmentSelect"
          dense
          hide-details="auto"
          :autofocus="autoFocus.isDepartment"
          v-model="internalValue.department"
          :items="departments"
          item-text="caption"
          item-value="id"
          :label="departmentLabel"
          :rules="reqdSelectValidation"
          :auto-select-first = "true"
          @change="updateDeptBalance"
        ></v-autocomplete>
      </v-col>
            <v-col cols="3">
              <v-text-field class="odtrans amount input-text-size"
                id="odTransAmountText"
                dense
                hide-details="auto"
                :autofocus="autoFocus.isAmount"
                v-model="internalValue.amount"
                :rules="amountValidation"
                label="Amount"
                ref="amount"
                @blur="updateForteConfig(); roundAmount();"
                @focus="focusAmount($event); amountToNumber();"
              ></v-text-field>
            </v-col>
            <v-col cols="3">
              <v-select class="odtrans status input-text-size"
                id="odTransStatusSelect"
                dense
                hide-details="auto"
                v-model="internalValue.status"
                :items="statuses"
                :rules="reqdSelectValidation"
                item-text="description"
                item-value="name"
                label="Status"
                @change="onStatusChange"
              ></v-select>
            </v-col>
            <v-col cols="3">
              <ClarionDateControl class="odtrans finalized input-text-size"
                id="odTransFinalPaidDate"
                dense
                hide-details="auto"
                v-model="internalValue.finalized"
                :isRequired="false"
                :isIsoDate="true"
                :isToggle="false"
                :label="`Paid Date`"
              />
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="3">
              <v-text-field class="odtrans remarks input-text-size"
                id="odTransRemarksText"
                dense
                hide-details="auto"
                v-uppercase=globalSettingItem.conEverythingToCaps
                v-model="internalValue.remarks"
                :label="remarkCaption"
                type="string"
              ></v-text-field>
            </v-col>
            <v-col cols="4" v-if="unregisteredOrDeposit">
              <v-select id="odTransItemDepositSelect"
                class="od-trans-itemdeposit-select input-text-size"
                dense
                hide-details="auto"
                v-model="internalValue.depositType"
                :items="clearableDepositTypes"
                :rules="reqdSelectValidation"
                item-text="description"
                item-value="name"
                label="Itemized deposit type? (if any)"
                @change="validateForm(); updateForteConfig();"
              ></v-select>
            </v-col>
            <v-col cols="2">
              <v-checkbox class="odtrans isCorrected input-text-size"
                id="odTransIsCorrected"
                dense
                hide-details
                v-model="internalValue.isCorrected"
                label="Corrected"
                @change="onCorrectedChange"
              ></v-checkbox>
            </v-col>
            <v-col cols="3">
              <v-text-field class="odtrans correctedAmount input-text-size"
                id="odTransCorrectedAmountText"
                dense
                hide-details="auto"
                v-model="internalValue.correctedAmount"
                :disabled="!internalValue.isCorrected"
                :rules="correctedAmountValidation"
                label="Corrected Amount"
                @blur="roundCorrectedAmount"
                @focus="focusAmount($event); correctedAmountToNumber()"
              ></v-text-field>
            </v-col>
            <v-col cols="3" v-if="unregisteredOrDeposit && odSettingItem.isBnakOverrideOption">
              <v-select id="odTransItemOdBankSelect"
                class="od-trans-itemBank-select input-text-size"
                dense
                hide-details="auto"
                v-model="internalValue.odBank"
                :items="banks"
                item-text="bankName"
                item-value="_id"
                label="OD Bank"
              ></v-select>
            </v-col>
          </v-row>
          <v-row v-if="unregisteredOrDeposit">
            <v-col cols="2">
              <v-checkbox id="odTransIsTrust"
                class="odtrans isTrust input-text-size"
                dense
                hide-details
                v-model="internalValue.isTrust"
                label="Trust"
                @change="validateForm()"
              ></v-checkbox>
            </v-col>
            <v-col cols="10" v-if="isTrustFlag" class="odtrans trust-detail-group">
              <v-row>
                <v-col cols="3">
                  <v-autocomplete id="odTransTrustTypeSelect"
                    class="odtrans trust-type input-text-size"
                    dense
                    hide-details="auto"
                    v-model="internalValue.trustType"
                    :items="filteredTrustTypes"
                    item-text="trustType"
                    item-value="_id"
                    label="Trust Type"
                    no-data-text="None set up - optional"
                  ></v-autocomplete>
                </v-col>
                <v-col cols="3">
                  <v-checkbox
                    class="input-text-size"
                    dense
                    hide-details
                    v-model="internalValue.isPrePaidMobileHome"
                    label="Pre-Paid Mobile Home"
                  ></v-checkbox>
                </v-col>
                <v-col cols="6">
                  <v-text-field class="odtrans depositAddress input-text-size"
                    id="odTransDepositAddressText"
                    dense
                    hide-details="auto"
                    v-model="internalValue.depositAddress"
                    v-uppercase=globalSettingItem.conEverythingToCaps
                    label="Address"
                    type="string"
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
          <v-row v-if="unregisteredOrDeposit">
            <v-col cols="12" sm="12" md="12"
              v-if="isTrustFlag
                && odSettingItem.trustReconciliation
                && odSettingItem.selectedGateway !== 'payrix'">
              <TrustDetails id="odTransTrustDetails"
                class="od-trans-trust-details"
                v-model="internalValue.trustItems"
                :depositAmount="internalValue.amount"
                :depositData="internalValue"
                :trustTypeRecord="selectedTrustType"
                @forteResponse="forteResponse"
              />
            </v-col>
            <v-col cols="12" sm="12" md="12"
              v-if="isTrustFlag
                && odSettingItem.trustReconciliation
                && odSettingItem.selectedGateway === 'payrix'">
              <TrustDetails id="odTransTrustDetails"
                class="od-trans-trust-details"
                v-model="internalValue.trustItems"
                :depositAmount="internalValue.amount"
                :depositData="internalValue"
                :trustTypeRecord="selectedTrustType"
                @payrixSuccess="handlePayrixSuccess"
              />
            </v-col>
            <v-col cols="12" sm="12" md="12"
              v-if="internalValue.depositType === 'CASHCHECK'
                && odSettingItem.cashCheckReconciliation">
              <CashCheckDetails id="odTransCashCheckDetails"
                class="od-trans-cashCheck-details"
                v-model="internalValue.cashCheckItems"
                :depositAmount="internalValue.amount"
                :componentKey="componentKey"
                @triggerValidation="triggerValidation"
              />
            </v-col>
            <v-col cols="12" sm="12" md="12"
              v-if="internalValue.depositType === 'CREDIT'
                && odSettingItem.ccReconciliation
                && odSettingItem.selectedGateway !== 'payrix'">
              <CreditDetails id="odTransCreditDetails"
                class="od-trans-credit-details"
                v-model="internalValue.creditItems"
                :componentKey="componentKey"
                :depositAmount="internalValue.amount"
                :depositData="internalValue"
                @forteResponse="forteResponse"
              />
            </v-col>
            <v-col cols="12" sm="12" md="12"
              v-if="internalValue.depositType === 'CREDIT'
                && odSettingItem.ccReconciliation && odSettingItem.selectedGateway === 'payrix'">
              <CreditDetails id="odTransCreditDetails"
                class="od-trans-credit-details"
                v-model="internalValue.creditItems"
                :componentKey="componentKey"
                :depositAmount="internalValue.amount"
                :depositData="internalValue"
                @payrixSuccess="handlePayrixSuccess"
              />
            </v-col>
            <v-col cols="12" sm="12" md="12"
              v-if="internalValue.depositType === 'EFT'
                && odSettingItem.selectedGateway !== 'payrix'">
              <EFTDetails id="odTransEFTDetails"
                class="od-trans-eftdetails"
                v-model="internalValue.EFTItems"
                :depositAmount="internalValue.amount"
                :depositData="internalValue"
              />
            </v-col>
            <v-col cols="12" sm="12" md="12"
              v-if="internalValue.depositType === 'EFT'
                && odSettingItem.selectedGateway === 'payrix'">
              <EFTDetails id="odTransEFTDetails"
                class="od-trans-eftdetails"
                v-model="internalValue.EFTItems"
                :depositAmount="internalValue.amount"
                :depositData="internalValue"
                @payrixSuccess="handlePayrixSuccess"
              />
            </v-col>
          </v-row>
        </v-container>
      </v-form>
    </v-card-text>
    <v-card-actions>
      <v-btn id="odTransDepositTicket"
        class="odtrans depositticket"
        text color="blue darken-1"
        v-if="internalValue.type === 'DEPOSIT' || internalValue.type === 'UNREGISTERED'"
        @click="printReceipt('depositticket')"
      >
        <v-icon>{{icons.mdiPrinter}}</v-icon>
        Print (Deposit Ticket)
      </v-btn>
      <v-btn v-if="isTrustReceipt"
        class="odtrans trustreceipt"
        id="odTransTrustReceipt"
        text color="blue darken-1"
        @click="printReceipt('trustreceipt')"
      >
        <v-icon>{{icons.mdiPrinter}}</v-icon>
        Print (Trust Receipt)
      </v-btn>
      <v-spacer></v-spacer>
      <AuditDialog
        :identifier="internalValue.id"
        activatorCaption="Audit History"
      />
      <UploadFile id="odTransUploadFile"
        class="odtrans uploadFile"
        bucketName="OD"
        program="OD"
        parentObjectType="ODTransaction"
        :parentObjectId="internalValue.id"
        :itemIsSaved="!isNewItem">
        <template v-slot:activator="{ activatorProps }">
          <v-btn
            color="primary"
            text
            @contextmenu.prevent="imagesRightClick(activatorProps.rightClick)"
            @click="imagesLeftClick(activatorProps.leftClick)"
          >
            {{activatorProps.caption}}
          </v-btn>
        </template>
      </UploadFile>
      <v-btn class="odtrans close"
        id="odTransClose"
        text color="blue darken-1"
        @click="close"
      >
        Cancel
      </v-btn>
      <v-btn class="odtrans save"
        id="odTransSave"
        text color="blue darken-1"
        @click="save"
      >
        Save
      </v-btn>
      <div v-if="isNewItem">(Shift+Enter to Duplicate)</div>
    </v-card-actions>
  </v-card>
</template>
<script>
import {
  mapState,
  mapActions,
  mapGetters,
  mapMutations,
} from 'vuex';
import DepositVoucherMixin from './DepositVoucher.mixin';
import UploadFile from '../common/UploadFile.vue';
import CashCheckDetails from './CashCheckDetails.vue';
import CreditDetails from './CreditDetails.vue';
import EFTDetails from './EFTDetails.vue';
import TrustDetails from './TrustDetails.vue';
import ClarionDateControl from '../common/ClarionDateControl.vue';
import AuditDialog from '../common/audit/AuditDialog.vue';
import { fields } from './DepositVoucher.shared';
import { clients } from '../../util/clients';
import { isNotBeforeDate, toIsoDate } from '../../util/shared/tmc-global';
import { printerHandler } from '../../util/pdf-to-printer.util';
import GlobalToCaps from '../../util/shared/GlobalToCaps.mixin.vue';

const { backendRest } = clients.direct;

export default {
  name: 'DepositDetails',
  mixins: [DepositVoucherMixin, GlobalToCaps],
  components: {
    CashCheckDetails,
    CreditDetails,
    EFTDetails,
    TrustDetails,
    ClarionDateControl,
    UploadFile,
    AuditDialog,
  },
  data: () => ({
    bankCrudKey: 'sharedBank',
    fiscalYear: 0,
    componentKey: 0,
  }),
  props: {
    editedItem: {
      type: Object,
      default: () => ({
        ...fields(),
      }),
    },
  },
  mounted() {
    /* cleanup old forte config */
    this.setConfigData({});
    this.loadTrustTypes({});
  },
  computed: {
    internalValue: {
      get() {
        return this.editedItem;
      },
      set(value) {
        this.$emit('input', value);
      },
    },
    ...mapGetters('base/crud', [
      'items',
    ]),
    clearableDepositTypes() {
      if (this.depositTypes === undefined) {
        return undefined;
      }
      return [{ name: '', description: '' }, ...this.depositTypes];
    },
    filteredTrustTypes() {
      const departmentTrustTypes = (this.trustTypes || []).filter(
        (trustType) => trustType.department === this.internalValue.department,
      );
      if (!departmentTrustTypes.length) {
        return [];
      }
      return [{ _id: null, trustType: '' }, ...departmentTrustTypes];
    },
    selectedTrustType() {
      return (this.trustTypes || []).find((t) => t._id === this.internalValue.trustType);
    },
    banks() {
      const bankObjs = this.items(this.bankCrudKey) || [];
      const odBanks = bankObjs.filter((bank) => bank.isOD === true);
      return [{ _id: null, bankName: '' }, ...odBanks];
    },
    ...mapState({
      depositTypes: (state) => state.enums.ODDepositEnum,
      odSettingItem: (state) => state.SystemConfig.odSettingItem,
      globalSettingItem: (state) => state.SystemConfig.globalSettingItem,
      paymentSettingItem: (state) => state.SystemConfig.paymentSettingItem,
      trustTypes: (state) => state.OD.trustTypes,
    }),
    ...mapGetters('user', [
      'getSettings',
    ]),
    remarkCaption() {
      return this.isTrustFlag ? 'Received Of' : 'Remarks';
    },
  },
  methods: {
    ...mapActions([
      'reAuth',
    ]),
    ...mapActions('OD', [
      'loadTrustTypes',
      'bumpTrustTypeLastNumberUsed',
    ]),
    ...mapActions('common/payrixPayment', [
      'setPaymentSuccessFlag',
    ]),
    ...mapMutations('ForteService', [
      'setConfigData',
    ]),
    async printReceipt(receiptType) {
      const { jwt } = await this.reAuth();
      const saveTrans = isNotBeforeDate(
        this.internalValue.register,
        this.todaysDate,
        (this.durationOfCorrection + 1),
      );
      let isReceiptPrintable;
      if (saveTrans || this.isNewItem) {
        isReceiptPrintable = await this.internalSave(undefined, false);
      } else {
        isReceiptPrintable = true;
      }
      if (isReceiptPrintable) {
        const { id } = this.internalValue;
        const url = `${backendRest.defaults.baseURL}/${receiptType}?odTransId=${id}&token=${jwt}`;
        // window.open(url, '_blank');
        // This method serves both buttons on the screen. Only the deposit ticket
        // is tray-routed — a trust receipt prints on ordinary paper and must not
        // inherit the deposit-slip tray, so it keeps the existing preview-tab
        // behaviour.
        const isDepositTicket = receiptType === 'depositticket';
        printerHandler(this, {
          url,
          reportName: isDepositTicket ? 'depositSlips' : '',
          deafultPrinterMode: isDepositTicket,
        });
      }
    },
    forteResponse(response) {
      this.internalValue.remarks += response;
    },
    async updateForteConfig() {
      if (
        this.unregisteredOrDeposit
        && this.internalValue.depositType === 'CREDIT'
      ) {
        // call forte event
        await this.$store.dispatch('ForteService/fetchForteConfig', {
          scope: 'odconfig',
          forteAmount: this.internalValue.amount.toString(),
        });
        this.componentKey += 1;
      }
      if (
        this.unregisteredOrDeposit
        && this.internalValue.depositType === 'CASHCHECK'
      ) {
        this.componentKey += 1;
      }
    },
    isItemCleared(value) {
      this.internalValue.creditItems = this.internalValue.creditItems.map((element) => {
        const creditItem = { ...element };
        creditItem.isCleared = value;
        return creditItem;
      });
    },
    onStatusChange() {
      if (this.internalValue.depositType === 'CREDIT') {
        if (this.internalValue.status === 'PAID') {
          return this.isItemCleared(true);
        }
        return this.isItemCleared(false);
      }
      return false;
    },
    triggerValidation() {
      const ref = this.$refs.form.validate();
      return ref;
    },
    registerDateValidation(value) {
      const fy = this.internalValue.fiscalYear;
      const fyStart = new Date(`${fy - 1}-07-01`);
      const fyEnd = new Date(`${fy}-07-07`);
      const registerDate = new Date(toIsoDate(value));
      return (
        (registerDate >= fyStart && registerDate <= fyEnd)
        || 'Register Date must fall within the fiscal year'
      );
    },
    async handlePayrixSuccess() {
      await this.save();
    },
  },
};
</script>

<style lang="sass" scoped>
  .DepositDetails
    .v-col
      padding-top: 4px
      padding-bottom: 4px
</style>
